import AsyncSelect from "react-select/async";
import { useContext, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { isEmpty } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal, ModalBody, ModalHeader } from "react-bootstrap";
import { useSelector } from "react-redux";

import Icon from "../../../components/Icon";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import TableFilter from "../../../components/TableFilter";
import SearchTable from "../../../components/SearchTable";
import EmptyData from "../../../components/EmptyData";
import UserAvatar from "../../../components/UserAvatar";
import PaginationComponent from "../../../components/Pagination";
import Badge from "../../../components/Badge";
import useUploadFile from "../../../hook/useUploadFile";
import ErrorMessage from "../../../components/ErrorMessage";
import AvatarCropper from "../../../components/AvatarCropper";
import DataExporter from "../../../components/DataExporter";
import Currency, {
  ConvertCurrency,
  ConvertCurrencyToText,
} from "../../../helpers/Currency/Currency";
import { productAdminContext } from "../../../context/AdminContext/ProductAdminContext/ProductAdminContext";
import { createProductValidation } from "../../../utils/validation/validation";
import {
  convertDate,
  numberSeparate,
  removeNumberSeparate,
} from "../../../utils";
import {
  convertProductTypeToText,
  convertRoleToPath,
  exporterData,
  productTypeItems,
} from "../../../utils/enum";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";

const ProductsAdmin = () => {
  const navigate = useNavigate();

  const auth = useSelector((state) => state.auth);
  const loader = useSelector((state) => state.loader);

  const { pathname } = useLocation();

  const { handleUploadFile } = useUploadFile();

  const {
    products,
    product,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    isActive,
    setIsActive,
    type,
    setType,
    pageCount,
    itemsCount,
    setItemsCount,
    pageNumber,
    setPageNumber,
    isShowModal,
    handleShowModal,
    handleHideModal,
    handleGetCategories,
    handleGetFields,
    handleGetProducts,
    handleCreateProduct,
    handleEditProduct,
    handleDeleteProduct,
    handleChangeProductActive,
  } = useContext(productAdminContext);

  useEffect(() => {
    handleGetProducts();
  }, [start, end, pageNumber, type, itemsCount, sort, pathname, isActive]);

  // Validation
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createProductValidation),
  });

  return (
    <>
      <SiteTitle title="محصولات" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
        productType={type}
        setProductType={setType}
        setIsActive={setIsActive}
        isActive={isActive}
      />

      <div className="card h-100 p-0 radius-12">
        <SearchTable
          endItem={
            <div className="d-flex justify-content-between align-items-center gap-2">
              {!isEmpty(products) && (
                <DataExporter
                  headers={exporterData.products.headers}
                  keys={exporterData.products.keys}
                  data={products}
                  fileName={exporterData.products.fileName}
                />
              )}
              <button
                className="btn btn-primary text-sm btn-sm radius-8 d-flex align-items-center gap-2"
                onClick={() => {
                  handleShowModal();
                  reset();
                }}
              >
                <Icon icon="ic:baseline-plus" className="text-xl" />
                افزودن محصول
              </button>
            </div>
          }
          filter={filter}
          setFilter={setFilter}
          onSearch={handleGetProducts}
        />
        <div className="card-body p-24">
          {isEmpty(products) ? (
            <EmptyData />
          ) : (
            <>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">محصول</th>
                      <th scope="col">کد</th>
                      <th scope="col">دسته بندی</th>
                      <th scope="col">نوع</th>
                      <th scope="col">
                        قیمت <Currency />
                      </th>
                      <th scope="col">
                        دوره تمدید <Badge color="primary">روز</Badge>
                      </th>
                      <th scope="col">تاریخ ایجاد</th>
                      <th scope="col">تاریخ آخرین ویرایش</th>
                      <th scope="col">وضعیت</th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((item, index) => (
                      <tr>
                        <td>{index + 1}</td>
                        <td>
                          <Link
                            to={`/${convertRoleToPath(auth.role)}/product/${
                              item?._id
                            }/product`}
                          >
                            <div className="d-flex align-items-center">
                              <UserAvatar
                                className="ms-2"
                                {...(item?.picture
                                  ? { image: item?.picture }
                                  : {
                                      icon: "solar:box-broken",
                                    })}
                              />
                              <div className="flex-grow-1">
                                <span className="text-md mb-0 fw-normal text-secondary-light">
                                  {item.title}
                                </span>
                              </div>
                            </div>
                          </Link>
                        </td>
                        <td>{item.code}</td>
                        <td>
                          {item.category?.parent && (
                            <>
                              <Link
                                to={`/${convertRoleToPath(
                                  auth.role
                                )}/category/${
                                  item.category?.parent?._id
                                }/category`}
                              >
                                {item.category?.parent?.title}
                              </Link>
                              {" / "}
                            </>
                          )}
                          <Link
                            to={`/${convertRoleToPath(auth.role)}/product/${
                              item.category?._id
                            }/category`}
                          >
                            {item.category?.title}
                          </Link>
                        </td>
                        <td>{convertProductTypeToText(item.type)}</td>
                        <td>
                          <ConvertCurrency price={item.price} />
                        </td>
                        <td>{item.renewalPeriod}</td>
                        <td>
                          {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                        </td>
                        <td>
                          {convertDate(item.updatedAt, "YYYY/MM/DD - HH:mm")}
                        </td>
                        <td>
                          <Badge color={item.isActive ? "success" : "danger"}>
                            {item.isActive ? "فعال" : "غیرفعال"}
                          </Badge>
                        </td>
                        <td>
                          <div className="dropdown">
                            <button
                              className="hover-bg-transparent"
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <Icon
                                icon="ph:dots-three-outline-fill"
                                className="menu-icon"
                              />
                            </button>
                            <ul className="dropdown-menu list">
                              <li
                                onClick={() =>
                                  navigate(
                                    `/${convertRoleToPath(auth.role)}/orders/${
                                      item._id
                                    }/product`
                                  )
                                }
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                              >
                                گزارش سفارشات محصول
                              </li>
                              <li
                                onClick={() =>
                                  navigate(
                                    `/${convertRoleToPath(
                                      auth.role
                                    )}/discount-code/${item._id}/product`
                                  )
                                }
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                              >
                                گزارش کدهای تخفیف محصول
                              </li>
                              <li
                                onClick={() => {
                                  reset();
                                  handleShowModal(item, setValue);
                                }}
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                              >
                                ویرایش
                              </li>

                              <li className="dropdown-divider" />

                              <li
                                onClick={() =>
                                  areYouSureMessage(
                                    () => handleChangeProductActive(item._id),
                                    `آیا از ${
                                      !item.isActive ? "فعال" : "غیرفعال"
                                    } کردن محصول ${item.title} مطمئنید؟`
                                  )
                                }
                                className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-${
                                  !item.isActive ? "success" : "danger"
                                }`}
                              >
                                {!item.isActive ? "فعال" : "غیرفعال"}
                              </li>
                              <li
                                onClick={() =>
                                  areYouSureMessage(
                                    () => handleDeleteProduct(item._id),
                                    `آیا از حذف محصول ${item.title} مطمئنید؟`
                                  )
                                }
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-danger"
                              >
                                حذف
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            </>
          )}
        </div>
      </div>

      <Modal
        show={isShowModal}
        className="modal-dialog-centered"
        size="lg"
        onHide={handleHideModal}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">
            {isEmpty(product) ? "افزودن محصول جدید" : "ویرایش محصول"}
          </span>
        </ModalHeader>
        <ModalBody>
          <div className="mb-40">
            <AvatarCropper
              onRemove={() => setValue("picture", "")}
              value={watch("picture")}
              onChange={(file) =>
                handleUploadFile(file, (e) => setValue("picture", e), 0)
              }
            />

            <ErrorMessage message={errors.picture?.message} />
          </div>
          <form
            autoComplete="off"
            onSubmit={handleSubmit(
              isEmpty(product) ? handleCreateProduct : handleEditProduct
            )}
          >
            <div className="row">
              <div className="col-12">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="title"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      عنوان
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="title"
                      placeholder="عنوان را وارد نمایید"
                      {...register("title")}
                    />
                  </div>

                  <ErrorMessage message={errors.title?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="code"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      کد
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="code"
                      placeholder="کد را وارد نمایید"
                      {...register("code")}
                    />
                  </div>

                  <ErrorMessage message={errors.code?.message} />
                </div>
              </div>

              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="type"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      نوع
                    </label>
                    <select
                      className="form-control radius-8"
                      {...register("type")}
                    >
                      <option value="" selected hidden>
                        نوع را انتخاب نمایید
                      </option>
                      {productTypeItems.map((item) => (
                        <option
                          key={item}
                          value={item}
                          selected={watch("type") === item}
                        >
                          {convertProductTypeToText(item)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <ErrorMessage message={errors.type?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="category"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      دسته بندی
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <AsyncSelect
                          defaultOptions
                          openMenuOnFocus
                          cacheOptions
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="لطفا دسته بندی را انتخاب نمایید"
                          isLoading={loader}
                          onBlur={onBlur}
                          noOptionsMessage={() => (
                            <p>دسته بندی برای نمایش یافت نشد</p>
                          )}
                          loadingMessage={() => <p>لطفا منتظر بمانید ...</p>}
                          loadOptions={(val, callback) => {
                            handleGetCategories(val, callback);
                          }}
                          onChange={(e) => {
                            onChange(e);
                            setValue("subCategory", null);
                          }}
                          value={value}
                          isClearable
                        />
                      )}
                      name="category"
                    />
                  </div>

                  <ErrorMessage
                    message={
                      errors.category?.message ||
                      errors.category?.value?.message
                    }
                  />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="subCategory"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      زیر دسته
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <AsyncSelect
                          defaultOptions
                          openMenuOnFocus
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="لطفا زیر دسته را انتخاب نمایید"
                          isLoading={loader}
                          onBlur={onBlur}
                          isDisabled={isEmpty(watch("category"))}
                          noOptionsMessage={() => (
                            <p>زیر دسته برای نمایش یافت نشد</p>
                          )}
                          loadingMessage={() => <p>لطفا منتظر بمانید ...</p>}
                          loadOptions={(val, callback) => {
                            handleGetCategories(
                              val,
                              callback,
                              watch("category")?.value
                            );
                          }}
                          onChange={(e) => onChange(e)}
                          value={value}
                          isClearable
                        />
                      )}
                      name="subCategory"
                    />
                  </div>

                  <ErrorMessage
                    message={
                      errors.subCategory?.message ||
                      errors.subCategory?.value?.message
                    }
                  />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="price"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      مبلغ <Currency />
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <input
                          min={0}
                          type="text"
                          placeholder="مبلغ را وارد نمایید"
                          className="form-control radius-8"
                          onChange={(e) => {
                            const value = removeNumberSeparate(e.target.value);
                            onChange(value);
                          }}
                          onBlur={onBlur}
                          value={numberSeparate(value)}
                          inputMode="decimal"
                        />
                      )}
                      name="price"
                    />
                    <ConvertCurrencyToText price={watch("price")} />
                  </div>

                  <ErrorMessage message={errors.price?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="renewalPeriod"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      دوره تمدید <Badge color="primary">روز</Badge>
                    </label>
                    <input
                      type="number"
                      inputMode="numeric"
                      className="form-control radius-8"
                      id="renewalPeriod"
                      placeholder="دوره تمدید را وارد نمایید"
                      {...register("renewalPeriod")}
                    />
                  </div>

                  <ErrorMessage message={errors.renewalPeriod?.message} />
                </div>
              </div>
              <div className="col-12">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="fields"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      فیلدها
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <AsyncSelect
                          defaultOptions
                          openMenuOnFocus
                          cacheOptions
                          isMulti
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="لطفا فیلدها را انتخاب نمایید"
                          isLoading={loader}
                          onBlur={onBlur}
                          noOptionsMessage={() => (
                            <p>فیلدها برای نمایش یافت نشد</p>
                          )}
                          loadingMessage={() => <p>لطفا منتظر بمانید ...</p>}
                          loadOptions={(val, callback) => {
                            handleGetFields(val, callback);
                          }}
                          onChange={(e) => {
                            onChange(e);
                          }}
                          value={value}
                          isClearable
                        />
                      )}
                      name="fields"
                    />
                  </div>

                  <ErrorMessage message={errors.fields?.message} />
                </div>
              </div>
              <div className="col-sm-12">
                <div className="mb-20">
                  <label
                    htmlFor="description"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    توضیحات
                  </label>
                  <textarea
                    name="description"
                    className="form-control radius-8"
                    id="description"
                    rows={5}
                    placeholder="توضیحات ..."
                    {...register("description")}
                  />
                </div>
              </div>
            </div>
            <div className="text-end mt-3">
              <button
                type="submit"
                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
              >
                {isEmpty(product) ? "افزودن" : "ویرایش"} محصول
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ProductsAdmin;
